import {ReactNode, useLayoutEffect, useRef, useState} from "react";

/**
 * A sidebar section (heading + collapsible content) that disappears completely when none of its entries is
 * actually rendered. Entry visibility is decided by the entries themselves (their own `Authorized` rules), so no
 * role / lab / division / customer policy is copied here: the group only looks at whether a navigation link
 * (`<a>`) exists in its content. Separators (`<hr>`) are not entries, and a collapsed group that has links stays visible.
 *
 * The children stay mounted while the group is hidden (inline `display: none`, not unmounting), because their
 * authorization updates after the parent rendered and when the context (lab, segment, impersonation) changes; a
 * MutationObserver re-evaluates the group whenever its content changes, so the result can never go stale.
 */
export default function SidebarGroup(props: SidebarGroupProps) {
    const {state, refs} = useSidebarGroup();
    return (
        <details ref={refs.group}
                 className="sidebar-group"
                 open={props.open}
                 style={state.hasEntries ? undefined : {display: "none"}}>
            <summary className="group-title">{props.title}</summary>
            <div className="group-content">
                {props.children}
            </div>
        </details>
    );
}

export function useSidebarGroup() {
    const group = useRef<HTMLDetailsElement>(null);
    // hidden until an entry is actually rendered, so an empty heading is never shown (not even for a frame)
    const [hasEntries, setHasEntries] = useState(false);

    useLayoutEffect(() => {
        const element = group.current;
        if (!element)
            return;
        const evaluate = () => setHasEntries(hasRenderedEntries(element));
        evaluate();
        // childList only: our own inline-style change is an attribute change and must not retrigger the observer
        const observer = new MutationObserver(evaluate);
        observer.observe(element, {childList: true, subtree: true});
        return () => observer.disconnect();
    }, []);

    return {
        state: {hasEntries},
        handlers: {},
        refs: {group},
        utilities: {hasRenderedEntries},
    };
}

/** True when the group's content contains at least one navigation link. A separator alone does not count. */
export function hasRenderedEntries(group: HTMLElement): boolean {
    return group.querySelector(".group-content a") !== null;
}

export interface SidebarGroupProps {
    title: ReactNode;
    /** initially expanded (the Orders group) */
    open?: boolean;
    children: ReactNode;
}
